import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldCheck, Upload, CheckCircle, XCircle, Clock, Loader2, Camera, Building2, IdCard, FileText, Globe, Copy, Link2, RefreshCw, Users, Unlink } from 'lucide-react';
import { TrustBadge } from '../components/TrustBadge';

const API = process.env.REACT_APP_BACKEND_URL;

// -- Country-specific config ------------------------------------
const COUNTRY_DOC_CONFIG = {
  UK: {
    label: '🇬🇧 United Kingdom',
    idDocs: [
      { type: 'dl_uk',       title: 'Driving Licence',   Icon: IdCard,   hint: 'A clear photo of the front of your UK driving licence.' },
      { type: 'passport',    title: 'Passport',          Icon: FileText, hint: 'A clear photo of your passport bio page.' },
    ],
    business: true,
  },
  LK: {
    label: '🇱🇰 Sri Lanka',
    idDocs: [
      { type: 'nic_lk',      title: 'National Identity Card (NIC)', Icon: IdCard, hint: 'A clear photo of the front of your NIC (old or new format).' },
      { type: 'passport',    title: 'Passport',                     Icon: FileText, hint: 'A clear photo of your passport bio page.' },
    ],
    business: true,
  },
};


// -- Small subcomponents ---------------------------------------
const StatusPill = ({ status }) => {
  const config = {
    approved: { label: 'Approved',   className: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle },
    pending:  { label: 'Pending',    className: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: Clock },
    rejected: { label: 'Rejected',   className: 'bg-red-100 text-red-800 border-red-300',             Icon: XCircle },
  }[status] || { label: status || '-', className: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Clock };
  const { Icon } = config;
  return (
    <span data-testid={`doc-status-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${config.className}`}>
      <Icon className="h-3 w-3" /> {config.label}
    </span>
  );
};

const DocUploadCard = ({ docType, title, hint, Icon, country, onUploaded, existingDoc }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('country', country);
    fd.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/trust/documents/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded - awaiting admin review');
      setFile(null);
      setPreview(null);
      onUploaded?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid={`doc-upload-${docType}`} className="border-2 border-dashed hover:border-blue-400 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          {existingDoc && <StatusPill status={existingDoc.status} />}
        </div>
        <p className="text-xs text-gray-500 mt-1">{hint}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {existingDoc?.status === 'approved' && (
          <Alert className="border-emerald-300 bg-emerald-50">
            <AlertDescription className="text-emerald-900 text-sm">✓ Approved - this counts toward your trust score.</AlertDescription>
          </Alert>
        )}
        {existingDoc?.status === 'rejected' && (
          <Alert className="border-red-300 bg-red-50">
            <AlertDescription className="text-red-900 text-sm">
              Rejected: {existingDoc.rejection_reason || 'no reason given'}. You may re-upload a clearer photo.
            </AlertDescription>
          </Alert>
        )}
        {(!existingDoc || existingDoc.status === 'rejected') && (
          <>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFile}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              data-testid={`doc-file-input-${docType}`}
            />
            {preview && <img src={preview} alt="preview" className="max-h-40 rounded border" />}
            <Button
              onClick={submit}
              disabled={!file || uploading}
              className="w-full"
              data-testid={`doc-upload-submit-${docType}`}
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? 'Uploading & scanning…' : 'Upload document'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};


const SelfieCard = ({ hasIdApproved, onDone }) => {
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const runCompare = async () => {
    if (!idFile || !selfieFile) {
      toast.error('Please choose both an ID photo and a selfie');
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('id_photo', idFile);
    fd.append('selfie', selfieFile);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/trust/documents/face-match`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.match) {
        toast.success(`Face verified - similarity ${(res.data.similarity * 100).toFixed(0)}%`);
        onDone?.();
      } else if (!res.data.id_face_detected || !res.data.selfie_face_detected) {
        toast.error('Could not detect a face in one of the images');
      } else {
        toast.error('Face did not match closely enough. Please retry with clearer photos.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Face match failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="selfie-verify-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-base">Selfie & ID Face Match</CardTitle>
        </div>
        <p className="text-xs text-gray-500">Take a clear selfie AND provide the photo page of your ID. We'll confirm the face matches.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">ID photo (must show your face on the document)</Label>
          <Input type="file" accept="image/*" onChange={(e) => setIdFile(e.target.files?.[0] || null)} data-testid="selfie-id-input" />
        </div>
        <div>
          <Label className="text-xs">Selfie (well-lit, face centred)</Label>
          <Input type="file" accept="image/*" capture="user" onChange={(e) => setSelfieFile(e.target.files?.[0] || null)} data-testid="selfie-live-input" />
        </div>
        <Button onClick={runCompare} disabled={busy} className="w-full" data-testid="selfie-verify-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
          {busy ? 'Comparing…' : 'Run face match'}
        </Button>
        {result && (
          <Alert className={result.match ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}>
            <AlertDescription className="text-sm">
              {result.match
                ? `✓ Faces matched - similarity ${(result.similarity * 100).toFixed(0)}%`
                : `✗ No match - similarity ${(result.similarity * 100).toFixed(0)}%`}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};


const CompaniesHouseCard = ({ onVerified }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/api/trust/companies-house/search?q=${encodeURIComponent(q)}`);
      if (!res.data?.success) {
        toast.error(res.data?.hint || 'Companies House unavailable - API key not configured');
        return;
      }
      setResults(res.data.results || []);
    } catch (e) {
      toast.error('Companies House lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (companyNumber) => {
    const token = localStorage.getItem('token');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/trust/companies-house/verify`, { company_number: companyNumber }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success(`Linked ${res.data.company.company_name} to your profile`);
        onVerified?.(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="companies-house-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-base">UK Companies House</CardTitle>
        </div>
        <p className="text-xs text-gray-500">Search for your company and link it to unlock business trust points.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company name or number" data-testid="ch-search-input" />
          <Button onClick={search} disabled={busy || !q.trim()} data-testid="ch-search-btn">Search</Button>
        </div>
        {results.map((r) => (
          <div key={r.company_number} className="flex items-center justify-between gap-2 p-2 border rounded" data-testid={`ch-result-${r.company_number}`}>
            <div className="text-sm">
              <div className="font-semibold">{r.company_name}</div>
              <div className="text-xs text-gray-500">{r.company_number} · {r.status} · {r.address}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => verify(r.company_number)} data-testid={`ch-link-${r.company_number}`}>Link this</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};


// -- Website DNS verification -----------------------------------
const WebsiteDnsCard = ({ onVerified }) => {
  const [status, setStatus] = useState(null);      // { expected_token, website_domain, website_verified }
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);      // last POST response
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/trust/verify/website-dns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
      if (res.data.website_domain) setDomain(res.data.website_domain);
    } catch (e) {
      // silent - card just shows without preloaded status
    }
  };

  useEffect(() => { load(); }, []);

  const copyToken = async () => {
    if (!status?.expected_token) return;
    try {
      await navigator.clipboard.writeText(status.expected_token);
      setCopied(true);
      toast.success('TXT record value copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Clipboard blocked - please copy manually');
    }
  };

  const check = async () => {
    if (!domain.trim()) {
      toast.error('Enter your domain first');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/trust/verify/website-dns`,
        { domain },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
      if (res.data.verified) {
        toast.success(`Website ${res.data.domain} verified - +6 trust points`);
        await load();
        onVerified?.();
      } else if (res.data.error === 'domain_not_found') {
        toast.error(`No DNS records found for ${res.data.domain}`);
      } else if (res.data.error) {
        toast.error(`DNS lookup issue: ${res.data.error}`);
      } else {
        toast.error('TXT record not found yet - allow up to 24h for DNS to propagate');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'DNS check failed');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Remove website verification? You can add a new domain after.')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/trust/verify/website-dns/unlink`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Website unlinked');
      setDomain('');
      setResult(null);
      await load();
      onVerified?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlink failed');
    } finally {
      setBusy(false);
    }
  };

  const isVerified = status?.website_verified;
  const token = status?.expected_token || 'zeilian-verify-XXXXXXXXXXXX';

  return (
    <Card data-testid="website-dns-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Website DNS Verification</CardTitle>
          </div>
          {isVerified && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="website-verified-badge">
              <CheckCircle className="h-3 w-3 mr-1" /> Verified
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Prove you own your business website by adding a TXT record to your DNS. Worth <b>+6</b> trust points.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isVerified && (
          <Alert className="border-emerald-300 bg-emerald-50">
            <AlertDescription className="text-emerald-900 text-sm flex items-center justify-between gap-2">
              <span>
                <CheckCircle className="h-4 w-4 inline mr-1" />
                <b>{status.website_domain}</b> is verified as yours.
              </span>
              <Button size="sm" variant="outline" onClick={unlink} disabled={busy} data-testid="website-unlink-btn">
                Unlink
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Step 1 - enter domain */}
        <div>
          <Label className="text-xs font-semibold text-gray-700">Step 1 - Your website domain</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="acme.co.uk"
              disabled={isVerified}
              data-testid="website-dns-domain-input"
            />
          </div>
          <p className="text-[11px] text-gray-500 mt-1">No https:// or paths - just the domain (e.g. <code>acme.co.uk</code>).</p>
        </div>

        {/* Step 2 - TXT record to add */}
        {!isVerified && (
          <div>
            <Label className="text-xs font-semibold text-gray-700">Step 2 - Add this TXT record to your DNS</Label>
            <div className="mt-1 border rounded-md bg-gray-50 p-3 text-xs space-y-2 font-mono">
              <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1">
                <span className="text-gray-500">Type</span><span>TXT</span>
                <span className="text-gray-500">Host / Name</span><span>@ &nbsp;<span className="text-gray-400 not-italic font-sans">(root of your domain)</span></span>
                <span className="text-gray-500">Value</span>
                <span className="flex items-center gap-2 break-all">
                  <code className="bg-white border px-1.5 py-0.5 rounded" data-testid="website-dns-token">{token}</code>
                  <Button size="sm" variant="ghost" onClick={copyToken} className="h-6 px-2" data-testid="website-dns-copy-btn">
                    {copied ? <CheckCircle className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </span>
                <span className="text-gray-500">TTL</span><span>3600 (or default)</span>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              Common providers: <b>Cloudflare</b>, <b>GoDaddy</b>, <b>Namecheap</b>, <b>Route 53</b>, <b>Google Domains</b>. Look for
              <i> DNS management</i> → <i>Add record</i> → <i>TXT</i>. DNS changes typically propagate within 5-30 min.
            </p>
          </div>
        )}

        {/* Step 3 - verify */}
        {!isVerified && (
          <div>
            <Label className="text-xs font-semibold text-gray-700">Step 3 - Check DNS</Label>
            <Button
              onClick={check}
              disabled={busy || !domain.trim()}
              className="w-full mt-1"
              data-testid="website-dns-check-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
              {busy ? 'Checking DNS…' : 'Check DNS record'}
            </Button>
          </div>
        )}

        {/* Result feedback */}
        {result && !isVerified && (
          <Alert className={result.verified ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}>
            <AlertDescription className="text-sm">
              {result.verified ? (
                <span className="text-emerald-900">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  Verified! Your trust score has been updated.
                </span>
              ) : (
                <div className="text-amber-900 space-y-1">
                  <div>
                    <XCircle className="h-4 w-4 inline mr-1" />
                    TXT record not detected yet on <b>{result.domain}</b>.
                  </div>
                  {result.txt_records_found?.length > 0 && (
                    <details className="text-xs text-gray-600 mt-1">
                      <summary className="cursor-pointer">See {result.txt_records_found.length} TXT record(s) we found</summary>
                      <ul className="mt-1 space-y-0.5 pl-4 list-disc font-mono">
                        {result.txt_records_found.map((r, i) => (
                          <li key={i} className="break-all">{r}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <div className="text-xs text-gray-600 pt-1">
                    DNS can take up to 24 hours to propagate. Try again in a few minutes with the
                    <button onClick={check} className="ml-1 underline inline-flex items-center gap-1 text-blue-700" data-testid="website-dns-retry-btn">
                      <RefreshCw className="h-3 w-3" /> retry button
                    </button>.
                  </div>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};


// -- Social account linking (Google + LinkedIn) -----------------
// Icons for providers (inline SVG so we don't depend on react-icons).
const GoogleIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l6-5.8C34.4 3.2 29.7 1 24 1 14.8 1 6.9 6.3 3 14l7 5.4C11.9 13.7 17.4 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.6c-.3 2.1-1.6 5.2-4.7 7.3l7.2 5.6c4.3-4 6.4-9.9 6.4-16.6z"/>
    <path fill="#FBBC05" d="M10 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7-5.4C1.5 17.4.5 20.6.5 24s1 6.6 2.5 9.4l7-4.8z"/>
    <path fill="#34A853" d="M24 46.5c6.5 0 11.9-2.1 15.8-5.7l-7.2-5.6c-2 1.4-4.7 2.3-8.6 2.3-6.6 0-12.1-4.2-14-10.4l-7 5.4C6.9 39.7 14.8 46.5 24 46.5z"/>
  </svg>
);

const LinkedInIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#0A66C2" d="M20.45 20.45h-3.55v-5.56c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.66H9.37V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.59 0 4.26 2.36 4.26 5.43v6.31zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0z"/>
  </svg>
);

const SocialTrustCard = ({ onChanged }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/trust/verify/social/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { load(); }, []);

  // Detect Emergent-Auth session_id in URL fragment on mount
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    // Clean the hash from URL history immediately
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    (async () => {
      setBusy(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/api/trust/verify/social/google/link`,
          { session_id: sessionId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data?.success) {
          toast.success(`Google linked (${res.data.email}) - +3 trust points`);
          await load();
          onChanged?.();
        } else {
          toast.error(`Google link failed: ${res.data?.error || 'unknown_error'}`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Google link failed');
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  // Detect LinkedIn callback ?social=linkedin_connected|linkedin_XXX
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const social = params.get('social');
    if (!social) return;
    // Clean URL
    params.delete('social');
    const newSearch = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    if (social === 'linkedin_connected') {
      toast.success('LinkedIn linked - +3 trust points');
      load();
      onChanged?.();
    } else if (social === 'cancelled' || social === 'error') {
      toast.error('LinkedIn connection cancelled');
    } else if (social.startsWith('linkedin_')) {
      toast.error(`LinkedIn link failed: ${social.replace('linkedin_', '')}`);
    }
  }, []);

  const connectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/get-verified';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const connectLinkedIn = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/trust/verify/social/linkedin/start`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        toast.error('LinkedIn not configured yet');
        setBusy(false);
      }
    } catch (e) {
      if (e?.response?.status === 501) {
        toast.error('LinkedIn is not yet configured on the server. Add LINKEDIN_CLIENT_ID/SECRET.');
      } else {
        toast.error(e?.response?.data?.detail || 'LinkedIn connect failed');
      }
      setBusy(false);
    }
  };

  const unlink = async (provider) => {
    if (!window.confirm(`Disconnect your ${provider === 'google' ? 'Google' : 'LinkedIn'} account? You will lose the +3 trust points.`)) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/trust/verify/social/${provider}/unlink`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${provider === 'google' ? 'Google' : 'LinkedIn'} unlinked`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlink failed');
    } finally {
      setBusy(false);
    }
  };

  const google = status?.google;
  const linkedin = status?.linkedin;
  const linkedinReady = status?.linkedin_configured;

  return (
    <Card data-testid="social-trust-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-base">Social Trust Boost</CardTitle>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Connect a Google and/or LinkedIn account for a quick <b>+3</b> trust points each. Individual sellers only.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Google row */}
        <div
          className="flex items-center justify-between gap-3 border rounded-md p-3"
          data-testid="social-row-google"
        >
          <div className="flex items-center gap-3 min-w-0">
            <GoogleIcon className="h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Google</div>
              {google?.verified ? (
                <div className="text-xs text-emerald-700 truncate">
                  <CheckCircle className="h-3 w-3 inline mr-1" />
                  {google.email}
                </div>
              ) : (
                <div className="text-xs text-gray-500">Sign in with Google to prove control of the email</div>
              )}
            </div>
          </div>
          {google?.verified ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                +3 pts
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unlink('google')}
                disabled={busy}
                data-testid="social-unlink-google"
                title="Disconnect Google"
              >
                <Unlink className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={connectGoogle}
              disabled={busy}
              data-testid="social-connect-google"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon className="mr-2 h-4 w-4" />}
              Connect Google
            </Button>
          )}
        </div>

        {/* LinkedIn row */}
        <div
          className="flex items-center justify-between gap-3 border rounded-md p-3"
          data-testid="social-row-linkedin"
        >
          <div className="flex items-center gap-3 min-w-0">
            <LinkedInIcon className="h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">LinkedIn</div>
              {linkedin?.verified ? (
                <div className="text-xs text-emerald-700 truncate">
                  <CheckCircle className="h-3 w-3 inline mr-1" />
                  {linkedin.email}
                </div>
              ) : linkedinReady ? (
                <div className="text-xs text-gray-500">Sign in to link a verified LinkedIn identity</div>
              ) : (
                <div className="text-xs text-amber-700">Coming soon - awaiting admin configuration</div>
              )}
            </div>
          </div>
          {linkedin?.verified ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                +3 pts
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unlink('linkedin')}
                disabled={busy}
                data-testid="social-unlink-linkedin"
                title="Disconnect LinkedIn"
              >
                <Unlink className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={connectLinkedIn}
              disabled={busy || !linkedinReady}
              data-testid="social-connect-linkedin"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LinkedInIcon className="mr-2 h-4 w-4" />}
              {linkedinReady ? 'Connect LinkedIn' : 'Not configured'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


// -- Main page --------------------------------------------------
export default function GetVerified() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [score, setScore] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/get-verified' } });
      return;
    }
    try {
      const [scoreRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/trust/score/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/trust/documents/mine`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setScore(scoreRes.data);
      setDocs(docsRes.data.documents || []);
    } catch (e) {
      toast.error('Unable to load verification status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch { /* ignore */ }
    refresh();
  }, []);

  const country = (score?.verification_country || user?.country || user?.verification_country || 'UK').toUpperCase();
  const cfg = COUNTRY_DOC_CONFIG[country] || COUNTRY_DOC_CONFIG.UK;
  const sellerType = score?.seller_type || user?.seller_type || 'individual';

  const docByType = (t) => docs.find((d) => d.doc_type === t);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="get-verified-page">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="get-verified-title">
              <ShieldCheck className="h-7 w-7 text-blue-600" /> Get Verified
            </h1>
            <p className="text-gray-600 mt-1">
              Prove your identity to unlock the <b>Verified</b> and <b>Gold</b> trust badges. Higher trust → more buyers.
            </p>
          </div>
          {score && <TrustBadge tier={score.tier} score={score.total} size="lg" />}
        </div>

        {/* Score summary */}
        {score && (
          <Card data-testid="score-summary">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="text-4xl font-bold">{Math.round(score.total)}<span className="text-lg text-gray-400">/100</span></div>
              <div className="flex-1">
                <Progress value={score.total} className="h-2" />
                <p className="text-xs text-gray-500 mt-1">
                  {score.tier === 'GOLD' && 'You have reached the highest tier.'}
                  {score.tier === 'VERIFIED' && 'Great - reach 80 for the Gold badge.'}
                  {score.tier === 'UNVERIFIED' && 'Upload documents below to reach 60 (Verified) or 80 (Gold).'}
                  {score.tier === 'BLOCKED' && 'Account is under review - contact support.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Country & seller type */}
        <Card data-testid="country-summary">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Globe className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Verification country:</span>
              <Badge data-testid="verification-country">{cfg.label}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Seller type:</span>
              <Badge variant="outline" data-testid="seller-type-badge">{sellerType === 'business' ? 'Business' : 'Individual'}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* ID document upload - country-strict */}
        <div>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <IdCard className="h-5 w-5 text-blue-600" /> 1. Upload a government-issued ID
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {cfg.idDocs.map((d) => (
              <DocUploadCard
                key={d.type}
                docType={d.type}
                title={d.title}
                hint={d.hint}
                Icon={d.Icon}
                country={country}
                existingDoc={docByType(d.type)}
                onUploaded={refresh}
              />
            ))}
          </div>
        </div>

        {/* Selfie face-match */}
        <div>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-600" /> 2. Selfie & ID face match
          </h2>
          <SelfieCard onDone={refresh} />
        </div>

        {/* Business - Companies House (UK only for now) */}
        {sellerType === 'business' && cfg.business && country === 'UK' && (
          <div>
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" /> 3. Verify your business
            </h2>
            <CompaniesHouseCard onVerified={refresh} />
          </div>
        )}

        {/* Website DNS verification - for business sellers */}
        {sellerType === 'business' && (
          <div>
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" /> 4. Verify your website
            </h2>
            <WebsiteDnsCard onVerified={refresh} />
          </div>
        )}

        {/* Social trust boost - individual sellers only (scoring engine awards socials only to individuals) */}
        {sellerType !== 'business' && (
          <div>
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" /> 3. Social trust boost
            </h2>
            <SocialTrustCard onChanged={refresh} />
          </div>
        )}

        <div className="text-center pt-2">
          <Button variant="outline" onClick={refresh} data-testid="refresh-status-btn">Refresh status</Button>
        </div>
      </div>
    </div>
  );
}
